// WHAT THE INSTALLED SDK CAN ACTUALLY DO — probed, not assumed.
//
// ---------------------------------------------------------------------------
// WHAT THIS MODULE MEASURES, AND WHY THE ANSWER MOVED
// ---------------------------------------------------------------------------
// The brief for this demo says: "`baseUrl` -> https://anna.dev.anshinhealth.dev,
// configurable via env/values so it can point elsewhere." The SDK's WORKING TREE always
// supported that — `packages/react/src/url.ts` takes a `baseUrl` argument and
// `AnnaLauncherProps` has a `baseUrl` prop.
//
// UNDER PUBLISHED 0.6.1, the ARTIFACT did not: `grep -aoc baseUrl dist/index.mjs` was `0`,
// neither `AnnaLauncherProps` nor `BuildAnnaUrlArgs` declared the prop, and the host came
// from a private two-literal `ANNA_URL_BY_ENV` map selected by `env` alone. The
// brand-profile layer was working-tree-only, so a host could not point 0.6.1 elsewhere.
//
// PUBLISHED 0.7.x CLOSED THAT GAP. Measured against the installed tarball from project 181:
// `baseUrl` appears in the bundle, the `.d.ts` now exports the brand-profile layer
// (`activeBrand`, `deriveBrandLabels`, `ANSHIN_SURFACE`/`MARTLET_SURFACE`,
// `resolveBrandSurface`, `BrandAppUrlError`, …), and the builder's own error says
// "Pass baseUrl explicitly". So the installed SDK now DOES honour a `baseUrl` override —
// and this module's job is still to prove that at runtime rather than trust a version.
//
// ---------------------------------------------------------------------------
// WHY A RUNTIME PROBE AND NOT A VERSION CHECK — vindicated by the 0.6.1 -> 0.7.x move
// ---------------------------------------------------------------------------
// `if (version === '0.6.1')` would be a presence check dressed as a capability check —
// SOC2-CONTROL-STANDARDS rule 3. It would have gone stale the moment 0.7.0 published:
// hardcoded "not supported" would keep claiming the gap after the gap was closed, with
// nothing failing. The probe tracked the real capability across the publish without a
// single edit — which is the whole argument for it.
//
// So this calls the function and looks at what comes back. The probe is correct for every
// version, including versions that do not exist yet, and it cannot pass for the wrong
// reason: the sentinel origin is one the map could not contain.

/**
 * The structural shape of the SDK's URL builder, carrying the `baseUrl` argument.
 *
 * Declared here rather than imported so the probe compiles against ANY published shape —
 * the historical 0.6.1 `.d.ts` that omitted `baseUrl` AND the current 0.7.x one that
 * declares it. Passing the property to a builder that might ignore it is exactly what the
 * probe measures, so the type must permit the call regardless of what the installed
 * `.d.ts` says — the probe's verdict is behavioural, not structural.
 */
export type WidenedUrlBuilder = (args: {
  src: string;
  tenant: string;
  env: 'dev' | 'prod';
  route?: string;
  trace?: string;
  baseUrl?: string;
}) => string;

/**
 * An origin the SDK's baked map cannot plausibly hold.
 *
 * `.invalid` is reserved by RFC 2606 and can never resolve, so if the probe's output
 * carries this origin the only possible explanation is that the builder honoured the
 * argument. A realistic-looking sentinel such as `https://anna.example.com` would leave
 * open the (remote) possibility of a coincidental match with a map entry; this does not.
 *
 * It is also inert if it ever escapes: a browser asked to open it fails to resolve
 * rather than reaching a host somebody else controls.
 */
export const BASE_URL_PROBE_ORIGIN = 'https://anna-sdk-demo-probe.invalid';

/** Probe arguments that are valid for every version of the builder. */
const PROBE_ARGS = {
  src: 'anna-sdk-demo-probe',
  tenant: '00000000-0000-4000-8000-000000000000',
  // A fixed route and trace keep the probe's output deterministic, so a failure is a
  // real finding rather than a flake. Without `trace` the builder generates one
  // containing a timestamp and six random characters.
  route: '/probe',
  trace: 'probe-0000000000000000',
} as const;

export interface SdkCapabilities {
  /** Does the installed builder honour a `baseUrl` argument? */
  baseUrlOverride: boolean;
  /**
   * The origin the installed SDK will actually open for `env`, read out of a real built
   * URL rather than from a constant in this repo.
   *
   * null when the builder threw — which the SDK does by design for a brand with no host
   * registered for that environment.
   */
  launcherOrigin: string | null;
  /** The builder's error message, when it threw. Rendered verbatim to the tester. */
  launcherError: string | null;
}

/**
 * Ask the installed SDK, by calling it, whether it honours `baseUrl`.
 *
 * Takes the builder as an argument rather than importing it so the test can drive both
 * answers from one source. A test that could only see the installed version would assert
 * whatever happens to be installed and pass either way — the same defect the SDK's own
 * `resolveAppBase` was split out to avoid.
 */
export function probeBaseUrlOverride(build: WidenedUrlBuilder): boolean {
  let url: string;
  try {
    url = build({ ...PROBE_ARGS, env: 'dev', baseUrl: BASE_URL_PROBE_ORIGIN });
  } catch {
    // A builder that throws on an unknown argument has not demonstrated support.
    return false;
  }
  try {
    return new URL(url).origin === BASE_URL_PROBE_ORIGIN;
  } catch {
    return false;
  }
}

/**
 * Ask the installed SDK which origin it will open for `env`.
 *
 * This is the value the demo displays as "the launcher will open", and it is read from
 * the SDK's own output. Displaying `config.baseUrl` there instead would be a screen that
 * agrees with the values file and disagrees with the product — the failure mode that
 * makes a UAT tool worse than no UAT tool.
 */
export function probeLauncherOrigin(
  build: WidenedUrlBuilder,
  env: 'dev' | 'prod',
): { origin: string | null; error: string | null } {
  let url: string;
  try {
    url = build({ ...PROBE_ARGS, env });
  } catch (err) {
    return { origin: null, error: err instanceof Error ? err.message : String(err) };
  }
  try {
    return { origin: new URL(url).origin, error: null };
  } catch {
    return { origin: null, error: `builder returned a non-URL: ${JSON.stringify(url)}` };
  }
}

/** Probe everything the UI reports about the installed SDK. */
export function probeSdk(build: WidenedUrlBuilder, env: 'dev' | 'prod'): SdkCapabilities {
  const launcher = probeLauncherOrigin(build, env);
  return {
    baseUrlOverride: probeBaseUrlOverride(build),
    launcherOrigin: launcher.origin,
    launcherError: launcher.error,
  };
}

/**
 * Is the configured target the one the launcher will actually open?
 *
 * Returns the mismatch rather than a boolean so the UI can name both sides. A tester who
 * sets `DEMO_BASE_URL` to a staging Anna, clicks the launcher and lands on dev would
 * otherwise have no way to tell that from the product misrouting them — and would file
 * the bug against the product.
 *
 * An unprobeable launcher (`launcherOrigin === null`) is NOT reported as agreement.
 * "I could not check" and "I checked and it agrees" are different answers, and a guard
 * that collapses them eventually reports the wrong one silently.
 */
export function targetMismatch(
  caps: SdkCapabilities,
  configuredBaseUrl: string,
): { configured: string; launcher: string | null } | null {
  if (caps.launcherOrigin === null) {
    return { configured: configuredBaseUrl, launcher: null };
  }
  if (caps.launcherOrigin === configuredBaseUrl) return null;
  return { configured: configuredBaseUrl, launcher: caps.launcherOrigin };
}
